export type EvidenceTone = "teal" | "coral" | "mist";

export const profile = {
  name: "陈沐彬",
  role: "测试开发工程师",
  focus: "质量效能与 AI 测试工具",
  seeking: "测试开发 / 质量效能 / AI 测试工具开发",
  experience: "5 年+研发与测试开发复合经验",
  value: "把测试执行、失败证据和发布判断接成一条可审计的质量链路。",
  summary:
    "5 年+研发与测试开发复合经验，现作为团队唯一测试人员，覆盖接口、Web UI、移动端、游戏 SDK、性能稳定性与发布质量保障。独立完成测试工具的方案设计、编码和 CI/CD 接入，并与研发、运维及业务团队协作完成规则确认、环境接入和上线验证；使用 Codex 与 Claude Code 辅助开发，并通过代码审查、自动化测试和静态检查验收产出。",
} as const;

export const navigation = [
  { label: "About", href: "#about" },
  { label: "Skills", href: "#capabilities" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Contact", href: "#contact" },
] as const;

export const evidenceRails = [
  {
    label: "发布门禁",
    value: "明确通过才继续发版",
    detail: "预发布触发回归，轮询最终结论",
    tone: "teal" as EvidenceTone,
  },
  {
    label: "路由评测",
    value: "97.6%",
    detail: "164 条固定样本 Top-1 基线准确率",
    tone: "coral" as EvidenceTone,
  },
  {
    label: "写接口",
    value: "355",
    detail: "query-first 与人工确认守卫",
    tone: "mist" as EvidenceTone,
  },
  {
    label: "Skill 审计",
    value: "112 / 112",
    detail: "结构审计通过",
    tone: "teal" as EvidenceTone,
  },
  {
    label: "框架单测",
    value: "353",
    detail: "R2 框架单元测试通过",
    tone: "coral" as EvidenceTone,
  },
  {
    label: "参数化用例",
    value: "166",
    detail: "8 个目录可收集，133 份 YAML 自检",
    tone: "mist" as EvidenceTone,
  },
  {
    label: "静态检查",
    value: "115 files",
    detail: "Mypy 检查源码文件无错误",
    tone: "teal" as EvidenceTone,
  },
] as const;

export const coverageRails = [
  { label: "接口", value: "API Automation", detail: "注册、登录、订单、支付、广告归因", tone: "mist" as EvidenceTone },
  { label: "Web UI", value: "Playwright", detail: "支付中心、官网与活动页", tone: "teal" as EvidenceTone },
  { label: "移动端", value: "Appium 2", detail: "登录、实名与充值支付", tone: "coral" as EvidenceTone },
  { label: "游戏 SDK", value: "UiAutomator2", detail: "Demo 入口与跨端执行", tone: "mist" as EvidenceTone },
  { label: "性能", value: "P95 / P99", detail: "负载、资源与版本基线", tone: "teal" as EvidenceTone },
  { label: "异常场景", value: "Charles", detail: "弱网、超时、重试与一致性", tone: "coral" as EvidenceTone },
  { label: "AI 工具", value: "Safe by design", detail: "确认、审计与 fail-closed", tone: "mist" as EvidenceTone },
] as const;

export const capabilities = [
  {
    number: "01",
    name: "跨端自动化",
    description:
      "以 Python、Pytest、Playwright、Selenium 4、Appium 2 和 Airtest + Poco 覆盖接口、Web UI、移动端与 Unity3D 核心流程；用 Page / Screen Object、数据驱动和失败证据归档保持用例可维护。",
    tools: ["Python", "Pytest", "Playwright", "Appium 2", "Allure"],
  },
  {
    number: "02",
    name: "测试平台工程化",
    description:
      "用 FastAPI、Flask、Node.js、REST API、MySQL、Docker 和 Linux 把执行入口、环境选择、任务调度、结果分析与报告发布组织成可复用服务。",
    tools: ["FastAPI", "Flask", "Node.js", "REST API", "Docker"],
  },
  {
    number: "03",
    name: "发布质量门禁",
    description:
      "接入 Jenkins、GitHub Actions、Allure 与飞书通知，在预发布后触发回归并轮询结论；只有明确通过才继续发布，同时保留环境元数据、失败分类与历史趋势。",
    tools: ["Jenkins", "GitHub Actions", "CI/CD", "Allure", "飞书"],
  },
  {
    number: "04",
    name: "性能与异常验证",
    description:
      "使用 Locust / JMeter 编排阶梯加压、突发流量和长稳场景，联动 Prometheus / Grafana 采集吞吐、P95/P99、错误率与资源指标，并以 Charles 验证弱网、超时、重试和数据一致性。",
    tools: ["Locust", "JMeter", "Prometheus", "Grafana", "Charles"],
  },
  {
    number: "05",
    name: "AI Agent 安全执行",
    description:
      "使用 Codex、Claude Code、Agent Skill / Plugin、工具调用和 JSON Schema 构建可审计的执行边界；采用 query-first、写操作确认、参数校验和 fail-closed 约束风险。",
    tools: ["Codex", "Claude Code", "JSON Schema", "路由评测", "fail-closed"],
  },
] as const;

export type Experience = {
  company: string;
  role: string;
  period: string;
  bullets: readonly string[];
};

export const experiences: readonly Experience[] = [
  {
    company: "深圳灿和兄弟网络科技有限公司",
    role: "平台测试工程师（测试开发方向）",
    period: "2022.01 - 至今",
    bullets: [
      "作为团队唯一测试人员，负责平台接口、游戏 SDK、支付中心、运营活动、后台与广告数据的全流程质量保障，覆盖需求评审、测试设计、缺陷跟踪、发布验证和线上复盘。",
      "独立开发接口、Web UI、移动端及结果分析工具，形成自动执行、证据归档、结果分析、发布判断的分层自动化体系；工具代码由本人完成，业务规则与环境接入由研发、运维及业务团队协作落地。",
      "将 R2 接入被测系统 CI/CD 发布流程，在预发布部署后触发接口回归并轮询最终结果，仅在明确通过时继续发布；结合性能基线与 Charles 异常场景验证登录、订单、支付等核心链路。",
      "使用 Codex 与 Claude Code 开发及验收测试工具，独立交付游戏后台 Agent Skills 平台，并以写操作确认、参数校验、单测和路由评测约束执行风险。",
    ],
  },
  {
    company: "深圳前海百递网络有限公司（快递 100）",
    role: "Java 开发工程师",
    period: "2021.03 - 2021.12",
    bullets: [
      "负责合作方物流查询渠道的模块开发，完成 API 接入、轨迹数据解析、清洗和统一封装；对无开放接口的渠道使用 HttpClient + Jsoup 采集并提取物流节点。",
      "参与 Spring Boot + MyBatis-Plus 订阅统计模块开发，以 Bitmap 管理订阅开关、Apache POI 导出统计数据，并使用 Postman 验证接口、Jenkins 持续集成。",
      "编写对外 API 文档和问题处理方案，为企业及电商客户提供接入支持，推动渠道联调、问题修复和上线验证闭环。",
    ],
  },
] as const;

export const education = {
  school: "东莞理工学院",
  major: "网络工程（本科）",
  period: "2017 - 2021",
} as const;

export type Project = {
  slug: string;
  number: string;
  title: string;
  shortTitle: string;
  ownership: string;
  period: string;
  stack: readonly string[];
  summary: string;
  background: string;
  responsibility: string;
  approach: readonly string[];
  challenges: string;
  value: string;
  metrics: readonly string[];
  flow: readonly string[];
  boundary: string;
};

export const projects: readonly Project[] = [
  {
    slug: "r2-release-gate",
    number: "01",
    title: "R2 游戏业务接口自动化与发布质量门禁服务",
    shortTitle: "R2 发布质量门禁",
    ownership: "独立开发与维护",
    period: "2022.03 - 至今",
    stack: ["Python 3.10", "Pytest", "Flask / Waitress", "APScheduler", "Requests", "MySQL", "Allure", "飞书", "Docker Compose", "Jenkins"],
    summary: "把多条游戏业务线的接口回归、结果分析和发版判断组织成可重复执行的服务。",
    background: "覆盖注册、登录、实名、角色、订单、支付、发货、积分、抽奖和广告归因等核心链路。",
    responsibility: "独立开发与维护测试框架、执行服务、结果分析和 CI/CD 接入；业务规则与环境接入由研发、运维及业务团队协作确认。",
    approach: [
      "以 Pytest 参数化、fixture 与 YAML 建设数据驱动测试；将同构业务线抽象为声明式 ServicePlan，统一环境校验、HTTP/cron 触发、队列去重和报告发布。",
      "通过独立 Pytest 子进程和 R2_RUN_CONTEXT 传递执行上下文；用业务锁与入队锁实现同线串行、跨线并行和原子去重。",
      "封装线程隔离的 Requests Session 与连接池，仅重试连接阶段失败，避免注册、下单和支付等非幂等请求重复执行。",
      "统一采集通过、失败、错误、跳过、成功率与耗时，生成带环境元数据、失败分类及同环境历史趋势的 Allure 报告，并集成飞书通知、敏感信息脱敏和跨进程安全落盘。",
    ],
    challenges: "在多业务线并行执行、非幂等请求和跨进程报告落盘同时存在时，保持任务边界、执行顺序和结果可信。",
    value: "提供基于 service + tag 的回归触发与 /getTestResult 查询接口，供被测系统 Jenkins 在预发布后触发并轮询结论，仅明确通过时继续发版。",
    metrics: ["133 份 YAML 自检通过", "8 个用例目录可收集 166 条参数化用例", "353 项框架单测通过", "Mypy 检查 115 个源码文件无错误"],
    flow: ["预发布触发", "环境校验", "Pytest 执行", "证据归档", "轮询结论"],
    boundary: "项目成果按简历记录的框架基线呈现；业务规则、环境接入和上线验证包含跨团队协作，不将协作成果全部归为个人成果。",
  },
  {
    slug: "agent-skills-gateway",
    number: "02",
    title: "游戏后台 Agent Skills 安全操作平台",
    shortTitle: "Agent Skills 安全平台",
    ownership: "独立开发",
    period: "2026.06 - 2026.08",
    stack: ["Node.js 20", "ES Modules", "Codex / Claude Plugin", "REST API", "JSON Schema", "GitHub Actions"],
    summary: "为游戏后台工具调用建立统一网关，把高风险写操作变成可预览、可确认、可审计的执行流程。",
    background: "面向玩家、订单、封禁、礼包码、广告和客服等业务域建设双端插件与 Skill。",
    responsibility: "独立完成插件组织、统一网关、路由评测、结构审计、单测和 Marketplace 一致性门禁。",
    approach: [
      "建设 23 组 Codex / Claude 双端插件、112 个 Skill，以零依赖统一网关处理路由、环境选择、服务器解析和后台 API 调用。",
      "面向 355 条写接口实现 query-first、差异计算、完整预览、人工确认与动态 Schema 校验；未知方法默认按写操作拦截。",
      "建立网关单测、Skill 结构审计、Top-1 路由评测、写操作守卫与 Marketplace 一致性门禁。",
    ],
    challenges: "在工具调用可能触发后台写操作的场景下，先让操作者看到完整差异，再决定是否执行，并让未知方法默认进入关闭路径。",
    value: "以 fail-closed 策略降低误操作风险，形成从路由、参数到执行确认的可审计边界。",
    metrics: ["87 项网关单测通过", "112 / 112 Skill 结构审计通过", "164 条固定样本", "Top-1 路由评测基线准确率 97.6%"],
    flow: ["识别意图", "查询现状", "计算差异", "人工确认", "受控写入"],
    boundary: "97.6% 是 164 条固定样本的 Top-1 路由评测基线准确率，不表述为线上准确率或生产成功率。",
  },
  {
    slug: "performance-baseline",
    number: "03",
    title: "游戏业务性能基线与异常场景验证",
    shortTitle: "性能基线与异常验证",
    ownership: "独立开发",
    period: "2025.09 - 2025.12",
    stack: ["Python", "Locust / JMeter", "Prometheus / Grafana", "Charles", "Docker", "Jenkins"],
    summary: "把核心业务链路的负载、资源和弱网异常放进同一套可复测的验证路径。",
    background: "围绕登录、角色、下单、支付和回调链路，覆盖阶梯加压、突发流量与长时间稳定性场景。",
    responsibility: "独立编排压测脚本、指标采集、版本基线、弱网场景和 Jenkins 证据归档。",
    approach: [
      "使用 Locust / JMeter 编排链路并以数据池隔离并发账号和订单。",
      "联动 Prometheus + Grafana 采集吞吐量、P95/P99、错误率及服务资源指标，按版本留存性能基线。",
      "使用 Charles Throttle 模拟高延迟、低带宽等弱网条件，通过 Breakpoints / Rewrite 修改请求与响应。",
    ],
    challenges: "让性能数据与真实业务状态、异常返回和恢复行为对应，而不只得到孤立的压测曲线。",
    value: "为容量评估和发布风险判断提供版本化基线；验证 Web 与游戏 SDK 在超时、异常返回和恢复场景下的提示、重试及数据一致性。",
    metrics: ["阶梯加压、突发流量、长稳场景", "吞吐 / P95 / P99 / 错误率 / 资源指标", "复测证据归档至 Jenkins"],
    flow: ["准备数据池", "编排负载", "采集指标", "注入异常", "归档复测"],
    boundary: "简历未提供性能提升百分比或容量结论，页面只展示验证范围与指标类型，不推导额外 ROI。",
  },
  {
    slug: "web-sdk-automation",
    number: "04",
    title: "Web UI 与游戏 SDK 自动化测试",
    shortTitle: "Web / SDK 自动化",
    ownership: "独立开发",
    period: "2023.04-06 / 2024.05-08",
    stack: ["Python", "Pytest", "Playwright", "Appium 2", "Selenium 4", "FastAPI", "Allure", "Jenkins"],
    summary: "将 Web UI、移动端 SDK、Demo 入口和失败证据接入一条可重复执行的自动化路径。",
    background: "Web 阶段覆盖支付中心、官网和活动页；游戏 SDK 阶段覆盖登录、实名认证及充值支付。",
    responsibility: "独立设计用例组织、执行入口、证据保留和 Jenkins 接入，并兼容 Selenium 4 存量脚本。",
    approach: [
      "Web UI 以 Pytest + Page Object 组织用例，使用 Playwright 覆盖登录、账号 / 角色管理及下单支付链路。",
      "游戏 SDK 以 Appium 2 + UiAutomator2 和 Screen Object 执行；使用 FastAPI 开发 SDK Demo 上传与任务触发入口，支持选择环境和测试计划后一键执行。",
      "自动保留 Playwright Trace、截图、录屏与 Appium 日志，通过 Allure 集中展示失败证据并接入 Jenkins；使用 Airtest + Poco 探索 Unity3D 核心流程。",
    ],
    challenges: "同时处理 Web 与移动端执行模型、存量 Selenium 脚本和多种失败证据格式，让失败项能进入缺陷跟踪和复测闭环。",
    value: "建立从入口选择、跨端执行到失败证据展示的自动化工作流，便于重复验证和问题定位。",
    metrics: ["Web UI 与游戏 SDK 两个阶段", "Playwright Trace / 截图 / 录屏", "Appium 日志与 Allure 失败证据"],
    flow: ["选择环境", "选择计划", "跨端执行", "收集证据", "缺陷复测"],
    boundary: "项目时间按简历中的两个阶段保留为 2023.04-06 / 2024.05-08，不合并成连续任职区间。",
  },
] as const;

export const methodSteps = [
  { number: "01", title: "先确认风险", text: "在需求评审和环境接入阶段确认业务规则、写操作边界与可验证的通过条件。" },
  { number: "02", title: "再组织执行", text: "用数据驱动、ServicePlan、Page / Screen Object 和隔离上下文，让执行可重复、可并行。" },
  { number: "03", title: "证据跟着失败走", text: "Trace、截图、录屏、日志、Allure 趋势和环境元数据共同构成复盘入口。" },
  { number: "04", title: "结论才能推动发布", text: "CI/CD 只接受明确通过的结果；不确定的方法、参数或状态进入拦截和人工确认。" },
] as const;

export const resumeDownloadName = "陈沐彬-测试开发.pdf";
const resumeEmail = "13267717471@163.com";
const resumePhone = "13267717471";
const configuredEmail = import.meta.env.VITE_PUBLIC_EMAIL?.trim() || resumeEmail;
const configuredPhone = import.meta.env.VITE_PUBLIC_PHONE?.trim() || resumePhone;
const configuredContactUrl = import.meta.env.VITE_CONTACT_URL?.trim() || "";

export const publicEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(configuredEmail)
  ? configuredEmail
  : resumeEmail;
export const publicPhone = /^\+?[0-9\s()-]{7,20}$/.test(configuredPhone)
  ? configuredPhone
  : resumePhone;
export const publicContactUrl = /^https:\/\//i.test(configuredContactUrl)
  ? configuredContactUrl
  : null;
export const resumeHref = `/resume/${resumeDownloadName}`;
